/**
 * MAP THE COLUMNS (a tester's report, 2026-10-15). A statement Leveri could not read by its headings is shown as the
 * server found it in the work's records: its headings, its first lines, and Leveri's proposal of which column is which.
 * The person confirms with one click, or changes a column first; Leveri files the mapping as evidence in their name and
 * the request is answered through the same gate as any answer. The record itself is not changed, and the screen reads
 * nothing itself: what the mapping reads is decided by the server, which refuses a mapping that reads no lines.
 */
import { useState } from "react";
import { WorkActionResponse, type OpenRequest, type WorkFile } from "@leveri/contracts";
import { Button, Notice, Reading, Select } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";
import { useKeyring } from "./keys.js";

type Sheet = NonNullable<OpenRequest["mappableSheets"]>[number];
type Role = keyof Sheet["proposed"];
const ROLES: ReadonlyArray<{ role: Role; label: string }> = [
  { role: "date", label: "Date" },
  { role: "description", label: "Description" },
  { role: "moneyOut", label: "Money out" },
  { role: "moneyIn", label: "Money in" },
  { role: "amount", label: "Amount (signed, instead of money out and in)" },
  { role: "balance", label: "Balance" },
  { role: "reference", label: "Reference" },
];
const NUM = "[font-variant-numeric:tabular-nums]";

export interface MapColumnsProps {
  workId: string;
  request: OpenRequest;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}

export function MapColumns({ workId, request, onWork, onOutcome }: MapColumnsProps) {
  const sheets = request.mappableSheets ?? [];
  const [maps, setMaps] = useState<Array<Record<Role, number | null>>>(() => sheets.map((s) => ({ ...s.proposed })));
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const keyFor = useKeyring();
  if (sheets.length === 0) return null;
  const ready = maps.every((m) => m.date !== null && (m.moneyOut !== null || m.moneyIn !== null || m.amount !== null));

  function set(sheetIndex: number, role: Role, value: string) {
    const at = value === "" ? null : Number(value);
    setMaps((now) => now.map((m, i) => {
      if (i !== sheetIndex) return m;
      // A column means one thing: choosing it for this role frees it from any other.
      const next = { ...m };
      if (at !== null) for (const r of Object.keys(next) as Role[]) if (next[r] === at) next[r] = null;
      next[role] = at;
      return next;
    }));
  }

  async function send() {
    if (busy || !ready) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await call("POST", `/api/work/${workId}/requests/${request.requirementId}/columns`, WorkActionResponse, {
        mappings: sheets.map((s, i) => ({ evidenceReferenceId: s.evidenceReferenceId, sheetName: s.sheetName, columns: maps[i] })),
        // One mapping per request: pressing the button twice is one act.
        submissionKey: keyFor(`columns:${request.requirementId}`),
      });
      onOutcome(result.outcome);
      onWork(result.work);
    } catch (error) {
      setFailure(failureMessage(error));
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 border-t border-rule-soft pt-4" data-testid="map-columns">
      <p className="text-[13px] text-stone">
        Leveri could not tell which column is which from the headings. It has proposed a reading below — confirm it, or
        change a column first. The mapping is filed in your name; the statement itself is not changed.
      </p>
      {sheets.map((sheet, s) => (
        <div key={`${sheet.evidenceReferenceId}:${sheet.sheetName}`} className="mt-4">
          {sheets.length > 1 && <p className="text-[14px] font-medium text-ink">Sheet “{sheet.sheetName}”</p>}
          <p className="text-[12px] text-stone">{sheet.reason}</p>
          {/* The first lines, as the record prints them: wide, so it scrolls in its own box on a phone. */}
          <div className="mt-2 overflow-x-auto">
            <table className="w-full min-w-[480px] text-[12px]">
              <thead>
                <tr className="border-b border-rule text-left text-stone">
                  {sheet.headings.map((h, i) => <th key={`${i}:${h}`} className="py-1.5 pr-3 font-normal">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {sheet.sample.map((row, r) => (
                  <tr key={r} className="border-b border-rule-soft text-ink">
                    {row.map((cell, i) => <td key={i} className={`whitespace-nowrap py-1.5 pr-3 ${NUM}`}>{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {ROLES.map(({ role, label }) => (
              <Select key={role} label={label} value={maps[s]?.[role] ?? ""} disabled={busy} onChange={(e) => set(s, role, e.target.value)}
                data-testid={`map-${role}`}>
                <option value="">Not in this sheet</option>
                {sheet.headings.map((h, i) => <option key={`${i}:${h}`} value={i}>{h}</option>)}
              </Select>
            ))}
          </div>
        </div>
      ))}
      <div className="mt-4 flex flex-wrap items-center gap-3.5">
        <Button variant="secondary" disabled={busy || !ready} onClick={() => void send()} data-testid="map-columns-send">Read it this way</Button>
        {!ready && <span className="text-[13px] text-stone">Choose the date, and the money out and in (or one signed amount).</span>}
      </div>
      {busy && <div className="mt-3"><Reading>Filing the mapping, and carrying on with the work…</Reading></div>}
      {failure && <div className="mt-3"><Notice>{failure}</Notice></div>}
    </div>
  );
}
